import { useState, useEffect } from 'react';
import { ChevronDown, Building2, Shield } from 'lucide-react';
import { salonApi, type SalonInfo } from '../../api/salonApi';
import './SalonSelector.css';

interface SalonSelectorProps {
    currentSalonId: string | null;
    onSalonChange: (salonId: string) => void;
}

const getPlanBadgeClass = (plan: string) => {
    switch (plan) {
        case 'ENTERPRISE': return 'plan-badge enterprise';
        case 'PRO': return 'plan-badge pro';
        default: return 'plan-badge free';
    }
};

const SalonSelector = ({ currentSalonId, onSalonChange }: SalonSelectorProps) => {
    const [salons, setSalons] = useState<SalonInfo[]>([]);
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedSalon, setSelectedSalon] = useState<SalonInfo | null>(null);

    useEffect(() => {
        const loadSalons = async () => {
            try {
                const allSalons = await salonApi.getAllSalons();
                setSalons(allSalons);

                // Set current salon
                const current = allSalons.find(s => s.id === currentSalonId);
                setSelectedSalon(current || allSalons[0] || null);
            } catch (error) {
                console.error('Error loading salons:', error);
            } finally {
                setIsLoading(false);
            }
        };
        loadSalons();
    }, [currentSalonId]);

    const handleSelect = (salon: SalonInfo) => {
        setSelectedSalon(salon);
        setIsOpen(false);
        onSalonChange(salon.id);
    };

    if (isLoading) {
        return (
            <div className="salon-selector loading">
                <Building2 size={18} />
                <span>Cargando salones...</span>
            </div>
        );
    }

    return (
        <div className="salon-selector-container">
            <div className="salon-selector-badge">
                <Shield size={14} />
                <span>SUPER ADMIN</span>
            </div>
            <div className="salon-selector cursor-pointer" onClick={() => setIsOpen(!isOpen)}>
                <Building2 size={18} />
                <span className="salon-name">{selectedSalon?.name || 'Seleccionar salón'}</span>
                {selectedSalon && (
                    <span className={getPlanBadgeClass(selectedSalon.subscriptionPlan)}>
                        {selectedSalon.subscriptionPlan}
                    </span>
                )}
                <ChevronDown size={18} className={`chevron ${isOpen ? 'open' : ''}`} />
            </div>

            {isOpen && (
                <div className="salon-dropdown">
                    <div className="dropdown-header">
                        <span>Cambiar de salón</span>
                        <small>{salons.length} salones disponibles</small>
                    </div>
                    <div className="salon-list">
                        {salons.map(salon => (
                            <div
                                className={`salon-item cursor-pointer ${salon.id === selectedSalon?.id ? 'active' : ''}`}
                                onClick={() => handleSelect(salon)}
                            >
                                <div className="salon-info">
                                    <span className="salon-item-name">{salon.name}</span>
                                    <span className={getPlanBadgeClass(salon.subscriptionPlan)}>
                                        {salon.subscriptionPlan}
                                    </span>
                                </div>
                                <small className="salon-id">{salon.id.substring(0, 8)}...</small>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default SalonSelector;
